import React, { useState, useEffect, useRef, useCallback } from 'react';
import { patientApi } from '../services/api'; // Import the api service
import { formatPhoneNumber, handlePhoneNumberChange } from '../App'; // Import phone number formatting utilities
import { storeDeviceCheckInfo, getStoredDeviceCheckInfo } from '../utils/sessionStorage'; // Import localStorage utilities

/**
 * DeviceCheckDefault component for checking camera and microphone
 */
const DeviceCheckDefault = ({ onSuccess, onFailure, forceError = false, caseId, tokenData }) => {
  const [cameraStatus, setCameraStatus] = useState('pending');
  const [micStatus, setMicStatus] = useState('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorMessage2, setErrorMessage2] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [showPhoneVisitPopup, setShowPhoneVisitPopup] = useState(false);
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [bestPhoneNumber, setBestPhoneNumber] = useState('');
  const [secondBestPhoneNumber, setSecondBestPhoneNumber] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState(null);
  const [showContinueButton, setShowContinueButton] = useState(false); // New state for continue button
  const [phoneVisitConsent, setPhoneVisitConsent] = useState(false); // State for consent checkbox
  const [hideDeviceUI, setHideDeviceUI] = useState(false); // State to track if device UI should be hidden
  const [hideOptionsSection, setHideOptionsSection] = useState(false); // State to track if options section should be hidden
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Handle forced error state (from timeout in parent component)
  useEffect(() => {
    if (forceError) {
      setCameraStatus('error');
      setMicStatus('error');
      setErrorMessage('Device check timed out. Please check your camera and microphone permissions.');
      
      // Store comprehensive device check error state
      if (caseId) {
        const errorState = {
          cameraStatus: 'error',
          micStatus: 'error',
          errorMessage: 'Device check timed out. Please check your camera and microphone permissions.',
          errorType: 'Timeout'
        };
        storeDeviceCheckInfo(errorState, caseId);
      }
    }
  }, [forceError, caseId]);

  // Check devices and set up streams
  const checkDevices = useCallback(async () => {
    try {
      // Request camera and microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      
      // Check video tracks
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0) {
        setCameraStatus('success');
        if (videoRef.current) videoRef.current.srcObject = stream;
      } else {
        setCameraStatus('error');
        setErrorMessage('No camera detected');
      }
      
      // Check audio tracks
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        setMicStatus('success');
        setupAudioAnalysis(stream);
      } else {
        setMicStatus('error');
        setErrorMessage(prev => prev ? `${prev}, No microphone detected` : 'No microphone detected');
      }
      
      // If both camera and microphone are working, store success state
      if (videoTracks.length > 0 && audioTracks.length > 0 && caseId) {
        const successState = {
          cameraStatus: 'success',
          micStatus: 'success',
          errorMessage: '',
          errorType: ''
        };
        storeDeviceCheckInfo(successState, caseId);
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      
      if (error.name === 'NotAllowedError') {
        setErrorMessage('Permission denied. Please allow access to your camera and microphone.');
      } else if (error.name === 'NotFoundError') {
        setErrorMessage('Camera or microphone not found. Please check your devices.');
      } else {
        setErrorMessage(`Error accessing devices: ${error.message}`);
      }
      
      setCameraStatus('error');
      setMicStatus('error');
      
      // Store comprehensive device check error state
      if (caseId) {
        const errorState = {
          cameraStatus: 'error',
          micStatus: 'error',
          errorMessage: error.message || 'Device check failed',
          errorType: error.name || 'Error'
        };
        storeDeviceCheckInfo(errorState, caseId);
      }
      
      // Call onFailure prop if provided
      if (onFailure) {
        onFailure();
      }
    }
  }, []);
  
  // Initialize device checks
  useEffect(() => {
    checkDevices();
    
    // Feather icons initialization removed to reduce CPU usage
    
    // Cleanup function
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [checkDevices]);
  
  // Check for stored device check state when component mounts (for analytics only)
  useEffect(() => {
    if (caseId) {
      const storedDeviceCheck = getStoredDeviceCheckInfo(caseId);
      if (storedDeviceCheck) {
        // Only use stored state if it's from the SAME session (same browser, same device)
        // We can detect this by comparing user agent and checking if it's recent (< 1 hour)
        const isSameDevice = storedDeviceCheck.deviceInfo?.userAgent === navigator.userAgent;
        const isRecent = storedDeviceCheck.deviceInfo?.timestamp &&
          (new Date() - new Date(storedDeviceCheck.deviceInfo.timestamp)) < 43200000; // 12 hours
        
        if (isSameDevice && isRecent && storedDeviceCheck.status === 'success') {
          // Only pre-populate if it was a recent success on the same exact device
          console.log('Same device, recent success - pre-populating positive state');
          setCameraStatus(storedDeviceCheck.cameraStatus);
          setMicStatus(storedDeviceCheck.micStatus);
        } else {
          // Different device or old data - always run fresh checks but log for analytics
          console.log('Different device or stale data - running device checks again');
          console.log('Device comparison:', {
            stored: storedDeviceCheck.deviceInfo?.userAgent,
            current: navigator.userAgent,
            isSame: isSameDevice,
            isRecent: isRecent
          });
        }
      }
    }
  }, [caseId]);

  // Fetch patient details and prepopulate phone numbers
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (tokenData && tokenData.sub) {
        try {
          const response = await patientApi.getPatientDetails(tokenData.sub);
          
          if (response.data && response.data.patient) {
            const patient = response.data.patient;
            
            // Check if patient has phone numbers and prepopulate the fields
            if (patient.phone_number) {
              const formattedPhoneNumber = formatPhoneNumber(patient.phone_number);
              setBestPhoneNumber(formattedPhoneNumber);
              console.log('Prepopulated best phone number:', formattedPhoneNumber);
            }
            
            if (patient.phone_number_2) {
              const formattedSecondaryPhoneNumber = formatPhoneNumber(patient.phone_number_2);
              setSecondBestPhoneNumber(formattedSecondaryPhoneNumber);
              console.log('Prepopulated secondary phone number:', formattedSecondaryPhoneNumber);
            }
          }
        } catch (error) {
          console.error('Error fetching patient details:', error);
        }
      }
    };

    fetchPatientDetails();
  }, [tokenData]);

  // Check if both devices are working
  useEffect(() => {
    if (cameraStatus === 'success' && micStatus === 'success') {
      setIsAllChecked(true);
      const timer = setTimeout(() => onSuccess(), 2000);
      return () => clearTimeout(timer);
    }
  }, [cameraStatus, micStatus, onSuccess]);
  
  // Feather icons re-initialization removed to reduce CPU usage
  
  // Set up audio analysis
  const setupAudioAnalysis = (stream) => {
    try {
      // Create audio context and analyzer
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      
      // Create source from stream
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      // Set up data array for analysis
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;
      
      // Start analyzing
      const analyzeAudio = () => {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        
        // Calculate average volume level
        let sum = 0;
        for (let i = 0; i < dataArrayRef.current.length; i++) {
          sum += dataArrayRef.current[i];
        }
        const avg = sum / dataArrayRef.current.length;
        
        // Normalize to 0-100 range
        setAudioLevel(Math.min(100, Math.max(0, avg * 2)));
        
        // Continue analyzing
        animationFrameRef.current = requestAnimationFrame(analyzeAudio);
      };
      
      analyzeAudio();
    } catch (error) {
      console.error('Error setting up audio analysis:', error);
    }
  };
  
  // Retry device check
  const retryDeviceCheck = () => {
    setCameraStatus('pending');
    setMicStatus('pending');
    setErrorMessage('');
    setIsAllChecked(false);
    setHideDeviceUI(false); // Show device UI when retrying
    setHideOptionsSection(false); // Show options section when retrying
    
    // Clear stored device check error state
    if (caseId) {
      // We don't need to call clearStoredDeviceCheckError() here
      // because we'll overwrite it with new state after checkDevices()
      console.log('Clearing stored device check error state for retry');
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    checkDevices();
  };

  const handlePhoneVisitClick = () => {
    setShowPhoneVisitPopup(true);
    setShowSupportPopup(false); // Ensure support popup is hidden
    setConfirmationMessage(''); // Clear previous confirmation message
    setShowContinueButton(false); // Hide continue button
    setErrorMessage2(''); // Clear previous error message
    setPhoneVisitConsent(false); // Reset consent checkbox state
    setHideDeviceUI(true); // Hide device UI when user chooses phone visit
    setHideOptionsSection(true); // Hide options section when user chooses phone visit
  };

  const handleSupportClick = () => {
    setShowSupportPopup(true);
    setShowPhoneVisitPopup(false); // Ensure phone visit popup is hidden
    setConfirmationMessage(''); // Clear previous confirmation message
    setShowContinueButton(false); // Hide continue button
    setErrorMessage2(''); // Clear previous error message
    setHideDeviceUI(true); // Hide device UI when user chooses support
    setHideOptionsSection(true); // Hide options section when user chooses support
  };

  const handleContinue = () => { // New function to handle continue button click
    // Store the user's comprehensive alternative choice
    if (caseId) {
      const choiceState = {
        cameraStatus: 'alternative',
        micStatus: 'alternative',
        choiceType: showPhoneVisitPopup ? 'phone_visit' : 'support',
        phoneNumber: bestPhoneNumber,
        secondPhoneNumber: secondBestPhoneNumber || '',
        consentGiven: phoneVisitConsent,
        errorMessage: 'User chose alternative option due to device issues'
      };
      storeDeviceCheckInfo(choiceState, caseId);
      console.log('Stored comprehensive device check alternative choice');
    }
    
    onSuccess(); // Call the original onSuccess prop
    
    // Reset state but keep device UI and options section hidden
    setShowPhoneVisitPopup(false);
    setShowSupportPopup(false);
    setBestPhoneNumber('');
    setSecondBestPhoneNumber('');
    setConfirmationMessage(null);
    setShowContinueButton(false);
    setErrorMessage2('');
    setPhoneVisitConsent(false);
    // Note: We don't reset hideDeviceUI and hideOptionsSection here because we want to keep them hidden
  };

  const handlePopupSubmit = async () => { // Made function async for API call
    // Clear any previous error message
    setErrorMessage2('');

    // Store current popup state for validation
    const isPhoneVisit = showPhoneVisitPopup;

    // Check if consent is required (only for phone visit) and not given
    if (isPhoneVisit && !phoneVisitConsent) {
      setErrorMessage2('Consent is required to proceed with a phone visit.');
      return;
    }

    // Check if required field is empty
    if (!bestPhoneNumber || bestPhoneNumber.trim() === '') {
      setErrorMessage2('Best number to reach you is required.');
      return;
    }
    
    // Extract digits from formatted phone numbers for validation
    const bestPhoneDigits = bestPhoneNumber.replace(/\D/g, '');
    const secondBestPhoneDigits = secondBestPhoneNumber.replace(/\D/g, '');
    
    // Implement phone number validation (9 or 10 digits)
    if (bestPhoneDigits.length !== 9 && bestPhoneDigits.length !== 10) {
      setErrorMessage2('Please enter a valid 9 or 10 digit phone number.');
      return;
    }

    // Validate second phone number format if provided
    if (secondBestPhoneNumber && (secondBestPhoneDigits.length !== 9 && secondBestPhoneDigits.length !== 10)) {
      setErrorMessage2('Please enter a valid 9 or 10 digit phone number for the second number.');
      return;
    }
    
    // Only hide popups after all validation has passed
    setShowSupportPopup(false);
    setShowPhoneVisitPopup(false);

    // Create confirmation message content based on which popup was open
    const messageContent = showPhoneVisitPopup
      ? (
        <>
          <h4 className="card-title mb-4">It&apos;s done!</h4>
          <p className="mb-4">We will call you at {bestPhoneNumber} when your Doctor is ready. Please keep your ringer turned on, and answer even if it says Spam.</p>
          <p className="mb-4">Ankr system will ask you to verify year of birth before connecting.</p>
        </>
      )
      : (
        <>
          <h4 className="card-title mb-4">It&apos;s done!</h4>
          <p className="mb-4">We will call you at {bestPhoneNumber} in the next few minutes. Please keep your ringer turned on, and answer even if it says Spam.</p>
          <p className="mb-4">Ankr system will ask you to verify year of birth before connecting.</p>
        </>
      );

    setConfirmationMessage(messageContent);

    // Send phone number(s) to Core Server API
    try {
      // TODO: Replace with actual API endpoint and data structure
      const response = await patientApi.post('/api/submit-phone-visit', {
        bestPhoneNumber: bestPhoneDigits, // Send just the digits without formatting
        secondBestPhoneNumber: secondBestPhoneDigits || '', // Send just the digits without formatting
        requestType: showPhoneVisitPopup ? 'phone_visit' : 'contact_support',
      });
      console.log('API call successful:', response.data);
      // After successful submission and showing confirmation, show the continue button
      setShowContinueButton(true); // Show continue button on success
    } catch (error) {
      console.error('API call failed:', error);
      // Handle API error - show an error message to the user
      setErrorMessage2('Failed to submit request. Please try again or contact support.');
      // Optionally, do not show the continue button on API failure
      setShowContinueButton(false); // Hide continue button on failure
    }
  };

  const handlePopupCancel = () => {
    setShowPhoneVisitPopup(false);
    setShowSupportPopup(false);
    setBestPhoneNumber('');
    setSecondBestPhoneNumber('');
    setConfirmationMessage(null);
    setShowContinueButton(false); // Hide continue button on cancel
    setErrorMessage2(''); // Clear error message on cancel
    setPhoneVisitConsent(false); // Reset consent checkbox
    setHideDeviceUI(false); // Show device UI again when canceling
    setHideOptionsSection(false); // Show options section again when canceling
  };
  
  return (
    <div className="card">
      <div className="card-body">
        <h3 className="card-title mb-4">Check Your Devices</h3>
        
      {!hideDeviceUI && (
        <>
          <div className="mb-4">
            <p>We need to check your camera and microphone to ensure they&apos;re working properly for your telehealth visit.</p>
          </div>
          
          {errorMessage && (
            <div className="alert alert-danger mb-4">{errorMessage}</div>
          )}
          
          <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="fas fa-video me-2"></i>
                  Camera
                </h5>
                
                <div className="camera-container mb-3">
                  {cameraStatus === 'pending' && (
                    <div className="camera-overlay">
                      <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  
                  {cameraStatus === 'error' && (
                    <div className="camera-overlay">
                      <div className="text-center">
                        <i className="fas fa-exclamation-triangle fs-1 mb-2"></i>
                        <p>Camera not available</p>
                      </div>
                    </div>
                  )}
                  
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera-feed"
                  ></video>
                </div>
                
                <div className="text-center">
                  {cameraStatus === 'success' ? (
                    <span className="badge bg-success">
                      <i className="fas fa-check-circle me-1"></i>
                      Camera working
                    </span>
                  ) : cameraStatus === 'error' ? (
                    <span className="badge bg-danger">
                      <i className="fas fa-times-circle me-1"></i>
                      Camera not working
                    </span>
                  ) : (
                    <span className="badge bg-secondary">
                      <i className="fas fa-clock me-1"></i>
                      Checking camera...
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="fas fa-microphone me-2"></i>
                  Microphone
                </h5>
                
                <div className="mb-3">
                  <p>Speak into your microphone to test it:</p>
                  
                  <div className="progress mb-3" style={{ height: '30px' }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${audioLevel}%` }}
                      aria-valuenow={audioLevel}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
                
                <div className="text-center">
                  {micStatus === 'success' ? (
                    <span className="badge bg-success">
                      <i className="fas fa-check-circle me-1"></i>
                      Microphone working
                    </span>
                  ) : micStatus === 'error' ? (
                    <span className="badge bg-danger">
                      <i className="fas fa-times-circle me-1"></i>
                      Microphone not working
                    </span>
                  ) : (
                    <span className="badge bg-secondary">
                      <i className="fas fa-clock me-1"></i>
                      Checking microphone...
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
        )}
        
        {(cameraStatus === 'error' || micStatus === 'error') && (
          <>
            {!hideOptionsSection && (
              <div className="text-center mb-3">
                <h3 className="card-title strong">Looks like you are having issue with your camera or mic. Please choose an option:</h3>
                <button className="btn btn-secondary m-2" onClick={retryDeviceCheck}>
                  <i className="fas fa-sync me-2"></i>
                  Retry Device Check
                </button>
                <button className="btn btn-primary m-2" onClick={handlePhoneVisitClick}><i className="fas fa-phone me-2"></i>Do phone visit instead</button>
                <button className="btn btn-secondary m-2" onClick={handleSupportClick}><i className="fas fa-life-ring me-2"></i>Contact Ankr Support</button>
              </div>
            )}
            <div>
              {(showPhoneVisitPopup || showSupportPopup) && (
                <div className=""> {/* Container for mobile friendliness */}
                  <div className="card-body">
                    <div className="form-control row"> 
                      {errorMessage2 && (
                          <div className="alert alert-danger mt-4 mb-4">{errorMessage2}</div>
                      )}
                      {showPhoneVisitPopup && (<div className="card-title mb-4">We are sorry you are having technical issues. Let&apos;s get this converted into a phone visit.</div>)}
                      {showSupportPopup && (<div className="card-title mb-4">We are sorry you are having technical issues. Let&apos;s get you connected to our Support Desk.</div>)}
                      <div className="col-md-6 mb-3">
                        <div className="row align-items-center">
                          <div className="col-sm-5">
                            <label className="form-label mb-0">Best number to reach you*</label>
                          </div>
                          <div className="col-sm-7">
                            <input
                              type="tel"
                              className="form-control"
                              id="bestPhoneNumber"
                              name="bestPhoneNumber"
                              value={bestPhoneNumber}
                              onChange={(e) => handlePhoneNumberChange(e, setBestPhoneNumber)}
                              placeholder={bestPhoneNumber.length <= 9 ? "123-456-7890" : "(123) 456-7890"}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="row align-items-center">
                          <div className="col-sm-5">
                            <label htmlFor="secondBestPhoneNumber" className="form-label mb-0">Second best number (optional)</label>
                          </div>
                          <div className="col-sm-7">
                            <input
                              type="tel"
                              className="form-control"
                              id="secondBestPhoneNumber"
                              name="secondBestPhoneNumber"
                              value={secondBestPhoneNumber}
                              onChange={(e) => handlePhoneNumberChange(e, setSecondBestPhoneNumber)}
                              placeholder={secondBestPhoneNumber.length <= 9 ? "123-456-7890" : "(123) 456-7890"}
                            />
                          </div>
                        </div>
                      </div>
                      {showPhoneVisitPopup && (<div className="col-sm-12 mb-3">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="phoneVisitConsent"
                            name="phoneVisitConsent"
                            checked={phoneVisitConsent}
                            onChange={(e) => setPhoneVisitConsent(e.target.checked)}
                            required
                          />
                          <label className="form-check-label" htmlFor="phoneVisitConsent">
                            I consent to receive care over a phone call. I understand that my standard telemedicine visit charges will apply.
                          </label>
                        </div>
                      </div>)}
                      <div className="col-sm-6 mb-3">
                        {!showContinueButton && <button className="btn btn-primary" onClick={handlePopupSubmit}>Submit</button>} {/* Hide submit button after submission */}
                        <button className="btn btn-secondary" onClick={handlePopupCancel}>Cancel</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {confirmationMessage && (
                <div className="alert alert-success mb-4">
                  {confirmationMessage}
                  <button className="btn btn-primary" onClick={handleContinue}>Continue with next steps</button> {/* Show continue button after confirmation */}
                </div>
              )}
              
            </div>
          </>
        )}
        
        {isAllChecked && (
          <div className="alert alert-success text-center">
            <i className="fas fa-check-circle me-2"></i>
            All devices are working properly! Proceeding to the next step...
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceCheckDefault;